import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectCanPurchaseChecked,
  selectCanPurchaseNewPass,
  selectPassesSettled,
  selectPurchasePassReasonFlag,
} from "../../../store/multipleEntryPassSlice";
import { isPartnerClalit, isPartnerMaoflaamit, isPartnerOvdimBari } from "../../../store/partnerStore";
import promoDeco from "../../../img/svg/Decorative-homepage-banner-elements.svg";
import { PASS_STATE } from "../../hooks/usePassState";
import { ChevronLeftIcon, TicketIcon, TrophyIcon } from "./HomeIcons";

// Multi-entry-pass banner. The four cases come from the shared usePassState
// hook, the same machine the profile page uses.

// Active pass: the partner's pass artwork with only the remaining-entry counter
// laid over it — the title is part of the artwork itself. Same asset and overlay
// anchor as multipleEntryPassDetails, so the two screens read as one object.
function ActivePassCard({ t, pass, passArt, onClick }) {
  const total = pass?.PossibleNumberOfEntries ?? 0;
  // NumberOfEntries = entries already used.
  const remaining = total - (pass?.NumberOfEntries ?? 0);
  const noEntriesLeft = remaining <= 0;

  return (
    <button type="button" className="hm-v2-pass-card" onClick={onClick}>
      {passArt && <img src={passArt} alt="" className="hm-v2-pass-card-art" />}
      <span className="hm-v2-pass-card-counter">
        {!noEntriesLeft && <span className="hm-v2-pass-card-counter-label">{t("ENTRIES_REMAINING")}</span>}
        <span className={`hm-v2-pass-card-counter-value${noEntriesLeft ? " hm-v2-pass-card-counter-value--empty" : ""}`}>
          {noEntriesLeft ? t("NO_ENTRIES_LEFT") : t("ENTRIES_COUNT_OF_TOTAL", { remaining, total })}
        </span>
      </span>
    </button>
  );
}

// Expired / used-up pass ("איזה חודש מדהים!"): trophy badge + static copy, no
// chevron and no date per design. Still tappable — it opens the pass details.
function ExpiredPassCard({ t, onClick }) {
  return (
    <button type="button" className="hm-v2-pass" onClick={onClick}>
      <span className="hm-v2-pass-badge">
        <TrophyIcon />
      </span>
      <span className="hm-v2-pass-texts">
        <span className="hm-v2-pass-title">{t("HOME_PASS_EXPIRED_TITLE")}</span>
        <span className="hm-v2-pass-body">{t("HOME_PASS_EXPIRED_BODY")}</span>
      </span>
    </button>
  );
}

// Purchase promo ("משתלם לכרטס!"): home's own design — gradient card, ticket
// badge and chevron over the design's decorative-shapes overlay. The purchase
// summary keeps the shared PassPromoBanner; Profile has its own copy too.
function PromoPassCard({ t, onClick }) {
  return (
    <button type="button" className="hm-v2-promo" onClick={onClick}>
      <img src={promoDeco} alt="" aria-hidden="true" className="hm-v2-promo-deco" />
      <span className="hm-v2-promo-icon">
        <TicketIcon />
      </span>
      <span className="hm-v2-promo-texts">
        <span className="hm-v2-promo-title">{t("HOME_PASS_PROMO_TITLE")}</span>
        <span className="hm-v2-promo-body">{t("HOME_PASS_PROMO_BODY")}</span>
      </span>
      <span className="hm-v2-promo-chevron">
        <ChevronLeftIcon />
      </span>
    </button>
  );
}

function HomePassBanner({ passState, passArt }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // IsPassPurchasePossible from CanPassPurchasePossible (fetched by mainPage).
  // Gates only the promo, same rule as legacy home (mainPage.jsx): null (response
  // not landed yet) hides it too, so the promo never flashes at ineligible users.
  const canPurchaseNewPass = useSelector(selectCanPurchaseNewPass);
  // IsRegularOrderFound: the purchase is blocked by a regular (single-entry)
  // order this month, not by a completed pass — the expired copy would lie.
  const regularOrderFound = useSelector(selectPurchasePassReasonFlag);
  const passesSettled = useSelector(selectPassesSettled);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  // Same partner condition as the checkCanPurchasePass dispatch in useHomeData —
  // a partner that never fires the eligibility check must not wait on its answer.
  const eligibilityExpected = isClalit || isOvdimBari || isMaoflaamit;

  const state = passState?.state ?? PASS_STATE.HIDDEN;
  // No usable pass — never had one, or all expired/used up. Both invite a
  // purchase when eligible; the expired copy ("buy at full price or wait")
  // is only for users who can't buy a new one, so both states must hold the
  // skeleton until the eligibility answer lands.
  const noUsablePass = state === PASS_STATE.NONE || state === PASS_STATE.INACTIVE;
  const pending = !passesSettled || (noUsablePass && eligibilityExpected && !canPurchaseChecked);
  let content = null;

  if (!pending) {
    if (state === PASS_STATE.ACTIVE) {
      content = (
        <ActivePassCard t={t} pass={passState.pass} passArt={passArt} onClick={() => navigate("/multipleEntryPass")} />
      );
    } else if (noUsablePass && canPurchaseNewPass === true) {
      content = <PromoPassCard t={t} onClick={() => navigate("/discover-pass-card")} />;
    } else if (state === PASS_STATE.INACTIVE && regularOrderFound !== true) {
      // Same guard as legacy multipleEntryPass.jsx: a regular order blocking the
      // purchase is not a completed month — show nothing rather than the trophy.
      content = <ExpiredPassCard t={t} onClick={() => navigate("/multipleEntryPass")} />;
    }
  }

  if (content) {
    // --card compensates the active artwork's internal transparent canvas — the
    // other banners have no such padding and must not inherit its negative margins.
    return (
      <div className={`hm-v2-pass-slot${state === PASS_STATE.ACTIVE ? " hm-v2-pass-slot--card" : ""}`}>{content}</div>
    );
  }

  // No banner to show (yet). While the pass fetch — or the purchase-eligibility
  // check — is still in flight, a fixed-height skeleton holds the space so the
  // feed doesn't jump and nothing flashes at users who already hold a pass.
  // Once the answer settles as "nothing to show" (including a failed
  // fetch/check), the slot collapses; keeping the same element across the
  // class switch lets the height transition animate that collapse.
  return (
    <div className={`hm-v2-pass-slot ${pending ? "hm-v2-pass-slot--pending" : "hm-v2-pass-slot--empty"}`}>
      {pending && <div className="hm-v2-pass-skeleton" aria-hidden="true" />}
    </div>
  );
}

export default HomePassBanner;
